<template>
  <AppLayout>

    <!-- ── MOBILE LAYOUT ── -->
    <div class="md:hidden min-h-screen bg-slate-100 dark:bg-slate-950 pb-24">

      <!-- Flash toasts — fixed at top, above everything -->
      <div v-if="shouldShowFlash && ($page.props.flash?.notice || $page.props.flash?.alert)"
        class="fixed top-14 inset-x-4 z-50 flex flex-col gap-2 pointer-events-none">
        <div v-if="$page.props.flash?.notice"
          class="flex items-center gap-2 bg-emerald-500 text-white text-sm px-4 py-3 rounded-2xl shadow-xl pointer-events-auto">
          <svg class="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          {{ $page.props.flash.notice }}
        </div>
        <div v-if="$page.props.flash?.alert"
          class="flex items-center gap-2 bg-rose-500 text-white text-sm px-4 py-3 rounded-2xl shadow-xl pointer-events-auto">
          <svg class="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
          </svg>
          {{ $page.props.flash.alert }}
        </div>
      </div>

      <!-- Profile hero -->
      <div class="relative bg-gradient-to-br from-indigo-600 to-violet-600 pt-8 pb-20 px-6 overflow-hidden">
        <div class="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full"></div>
        <div class="absolute -bottom-8 -left-8 w-32 h-32 bg-white/10 rounded-full"></div>
        <div class="relative z-10 flex flex-col items-center text-center">
          <div class="w-16 h-16 rounded-full bg-white/20 border-2 border-white/40 flex items-center justify-center text-white font-bold text-2xl mb-3 shadow-lg">
            {{ userInitial }}
          </div>
          <p class="text-white font-bold text-base leading-tight">{{ $page.props.auth.user.email }}</p>
          <p class="text-indigo-200 text-xs mt-1">Master Ledger Account</p>
        </div>
      </div>

      <!-- Cards pulled up over hero -->
      <div class="px-4 -mt-8 space-y-5">

        <!-- Business Info section -->
        <div>
          <p class="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider px-1 mb-2">Business Information</p>
          <div class="bg-white dark:bg-slate-900 rounded-2xl overflow-hidden shadow-sm">
            <form @submit.prevent="submitProfile">
              <!-- Name -->
              <div class="px-4 pt-4 pb-3 border-b border-slate-100 dark:border-slate-800">
                <label class="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Your Name</label>
                <input v-model="profile.name" type="text" placeholder="e.g. Ahmed Farooqi"
                  class="w-full bg-transparent text-slate-800 dark:text-white placeholder-slate-300 text-sm mt-1
                         focus:outline-none" />
              </div>
              <!-- Business Name -->
              <div class="px-4 pt-3 pb-3 border-b border-slate-100 dark:border-slate-800">
                <label class="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Business Name</label>
                <input v-model="profile.business_name" type="text" placeholder="e.g. Master Hardware"
                  class="w-full bg-transparent text-slate-800 dark:text-white placeholder-slate-300 text-sm mt-1
                         focus:outline-none" />
              </div>
              <!-- Address -->
              <div class="px-4 pt-3 pb-3 border-b border-slate-100 dark:border-slate-800">
                <label class="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Business Address</label>
                <textarea v-model="profile.business_address" rows="2" placeholder="e.g. Shop 12, Main Bazar"
                  class="w-full bg-transparent text-slate-800 dark:text-white placeholder-slate-300 text-sm mt-1
                         focus:outline-none resize-none"></textarea>
              </div>
              <!-- Phone -->
              <div class="px-4 pt-3 pb-4">
                <label class="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Phone Number</label>
                <input v-model="profile.business_phone_number" type="text" placeholder="e.g. 0300-1234567"
                  class="w-full bg-transparent text-slate-800 dark:text-white placeholder-slate-300 text-sm mt-1
                         focus:outline-none" />
              </div>
              <!-- Save button -->
              <div class="px-4 pb-4">
                <button type="submit" :disabled="profileLoading"
                  class="w-full bg-indigo-600 disabled:opacity-60 active:scale-[0.98] active:bg-indigo-700
                         text-white font-bold rounded-2xl py-3.5 text-sm shadow-md shadow-indigo-200 dark:shadow-indigo-900 transition">
                  {{ profileLoading ? 'Saving…' : 'Save Business Info' }}
                </button>
              </div>
            </form>
          </div>
        </div>

        <!-- Appearance section -->
        <div>
          <p class="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider px-1 mb-2">Appearance</p>
          <div class="bg-white dark:bg-slate-900 rounded-2xl overflow-hidden shadow-sm">
            <button @click="toggleDark"
              class="w-full flex items-center gap-3 px-4 py-4 active:bg-slate-50 dark:active:bg-slate-800 transition">
              <div class="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                :class="dark ? 'bg-amber-100 dark:bg-amber-950' : 'bg-indigo-100 dark:bg-indigo-950'">
                <svg v-if="dark" class="w-5 h-5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 3v1m0 16v1m8.66-9h-1M4.34 12h-1m15.07-6.07-.71.71M6.34 17.66l-.71.71m12.02 0-.71-.71M6.34 6.34l-.71-.71M12 7a5 5 0 100 10A5 5 0 0012 7z"/>
                </svg>
                <svg v-else class="w-5 h-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/>
                </svg>
              </div>
              <div class="flex-1 min-w-0 text-left">
                <p class="text-sm font-semibold text-slate-800 dark:text-white leading-tight">{{ dark ? 'Light Mode' : 'Dark Mode' }}</p>
                <p class="text-xs text-slate-400 mt-0.5">Switch to {{ dark ? 'light' : 'dark' }} theme</p>
              </div>
              <svg class="w-5 h-5 text-slate-300 dark:text-slate-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Account Mode section (Mobile) -->
        <div>
          <p class="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider px-1 mb-2">Account Mode</p>
          <div class="bg-white dark:bg-slate-900 rounded-2xl overflow-hidden shadow-sm p-4 space-y-3">

            <!-- Business Mode Option -->
            <button @click="updateBusinessMode(true)" type="button"
              class="w-full flex items-start gap-3 p-4 border-2 rounded-xl transition-all text-left active:scale-[0.98]"
              :class="profile.business_mode === true ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30' : 'border-slate-300 dark:border-slate-700 active:border-blue-300 dark:active:border-blue-700'">
              <div class="flex-shrink-0 mt-0.5">
                <div class="w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all"
                  :class="profile.business_mode === true ? 'border-blue-500 bg-blue-500' : 'border-slate-300 dark:border-slate-600'">
                  <div v-if="profile.business_mode === true" class="w-2.5 h-2.5 rounded-full bg-white"></div>
                </div>
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 mb-1">
                  <svg class="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z"/>
                  </svg>
                  <p class="text-sm font-bold text-slate-900 dark:text-white">Business Mode</p>
                </div>
                <p class="text-xs text-slate-600 dark:text-slate-400">
                  Invoices, payments, customers & products
                </p>
              </div>
            </button>

            <!-- Personal Mode Option -->
            <button @click="updateBusinessMode(false)" type="button"
              class="w-full flex items-start gap-3 p-4 border-2 rounded-xl transition-all text-left active:scale-[0.98]"
              :class="profile.business_mode === false ? 'border-green-500 bg-green-50 dark:bg-green-950/30' : 'border-slate-300 dark:border-slate-700 active:border-green-300 dark:active:border-green-700'">
              <div class="flex-shrink-0 mt-0.5">
                <div class="w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all"
                  :class="profile.business_mode === false ? 'border-green-500 bg-green-500' : 'border-slate-300 dark:border-slate-600'">
                  <div v-if="profile.business_mode === false" class="w-2.5 h-2.5 rounded-full bg-white"></div>
                </div>
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 mb-1">
                  <svg class="w-4 h-4 text-green-600 dark:text-green-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"/>
                  </svg>
                  <p class="text-sm font-bold text-slate-900 dark:text-white">Personal Mode</p>
                </div>
                <p class="text-xs text-slate-600 dark:text-slate-400">
                  Income, expenses, banks & wealth tracking
                </p>
              </div>
            </button>

          </div>
        </div>

        <!-- Mobile App section -->
        <div>
          <p class="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider px-1 mb-2">Mobile App</p>
          <div class="bg-white dark:bg-slate-900 rounded-2xl overflow-hidden shadow-sm">

            <!-- Android row -->
            <a href="/apps/android/master_ledger.apk" download
              class="flex items-center gap-3 px-4 py-4 border-b border-slate-100 dark:border-slate-800 active:bg-slate-50 dark:active:bg-slate-800 transition">
              <div class="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center shrink-0 shadow-sm">
                <svg class="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.523 15.341a.75.75 0 01-.75-.75V9.41a.75.75 0 011.5 0v5.181a.75.75 0 01-.75.75zm-11.046 0a.75.75 0 01-.75-.75V9.41a.75.75 0 011.5 0v5.181a.75.75 0 01-.75.75zM8.5 7.5h7a4.5 4.5 0 014.5 4.5v3a4.5 4.5 0 01-4.5 4.5h-7A4.5 4.5 0 014 15V12a4.5 4.5 0 014.5-4.5zm1.25 5.25a.75.75 0 100-1.5.75.75 0 000 1.5zm4.5 0a.75.75 0 100-1.5.75.75 0 000 1.5zM8.318 6.475l-1.17-2.03a.375.375 0 01.65-.374l1.2 2.08A6.48 6.48 0 0112 5.5c1.08 0 2.1.263 2.996.648l1.2-2.08a.375.375 0 01.65.375l-1.17 2.03A6.503 6.503 0 0118.5 10H5.5a6.503 6.503 0 012.818-3.525z"/>
                </svg>
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-semibold text-slate-800 dark:text-white leading-tight">Android App</p>
                <p class="text-xs text-slate-400 mt-0.5">Tap to download APK · v1.0</p>
              </div>
              <div class="flex items-center gap-1 bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 text-xs font-bold px-2.5 py-1.5 rounded-xl shrink-0">
                <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"/>
                </svg>
                Download
              </div>
            </a>

            <!-- iOS row -->
            <div class="flex items-center gap-3 px-4 py-4 opacity-50">
              <div class="w-10 h-10 rounded-xl bg-slate-400 dark:bg-slate-600 flex items-center justify-center shrink-0 shadow-sm">
                <svg class="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-semibold text-slate-500 dark:text-slate-400 leading-tight">iPhone App</p>
                <p class="text-xs text-slate-400 mt-0.5">iOS · App Store</p>
              </div>
              <span class="text-xs font-bold px-2.5 py-1.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-400 shrink-0">Soon</span>
            </div>

            <!-- Install tip -->
            <div class="mx-4 mb-4 mt-1 p-3 bg-amber-50 dark:bg-amber-950/40 rounded-xl flex gap-2">
              <svg class="w-4 h-4 text-amber-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"/>
              </svg>
              <p class="text-xs text-amber-600 dark:text-amber-500 leading-relaxed">Enable <strong>Install unknown apps</strong> in Android Settings before installing.</p>
            </div>
          </div>
        </div>

        <!-- Change Password section -->
        <div>
          <p class="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider px-1 mb-2">Security</p>
          <div class="bg-white dark:bg-slate-900 rounded-2xl overflow-hidden shadow-sm">
            <form @submit.prevent="submit">
              <!-- Current Password -->
              <div class="px-4 pt-4 pb-3 border-b border-slate-100 dark:border-slate-800">
                <label class="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Current Password</label>
                <div class="flex items-center gap-2 mt-1">
                  <input v-model="form.current_password" :type="show.current ? 'text' : 'password'" placeholder="••••••••" autocomplete="current-password" required
                    class="flex-1 bg-transparent text-slate-800 dark:text-white placeholder-slate-300 text-sm focus:outline-none" />
                  <button type="button" @click="show.current = !show.current" class="text-slate-400 active:text-slate-600 shrink-0">
                    <svg v-if="show.current" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"/>
                    </svg>
                    <svg v-else class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"/><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                    </svg>
                  </button>
                </div>
              </div>
              <!-- New Password -->
              <div class="px-4 pt-3 pb-3 border-b border-slate-100 dark:border-slate-800">
                <label class="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">New Password</label>
                <div class="flex items-center gap-2 mt-1">
                  <input v-model="form.password" :type="show.password ? 'text' : 'password'" placeholder="••••••••" autocomplete="new-password" required
                    class="flex-1 bg-transparent text-slate-800 dark:text-white placeholder-slate-300 text-sm focus:outline-none" />
                  <button type="button" @click="show.password = !show.password" class="text-slate-400 active:text-slate-600 shrink-0">
                    <svg v-if="show.password" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"/>
                    </svg>
                    <svg v-else class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"/><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                    </svg>
                  </button>
                </div>
              </div>
              <!-- Confirm Password -->
              <div class="px-4 pt-3 pb-3 border-b border-slate-100 dark:border-slate-800">
                <label class="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Confirm New Password</label>
                <div class="flex items-center gap-2 mt-1">
                  <input v-model="form.password_confirmation" :type="show.confirm ? 'text' : 'password'" placeholder="••••••••" autocomplete="new-password" required
                    class="flex-1 bg-transparent text-slate-800 dark:text-white placeholder-slate-300 text-sm focus:outline-none" />
                  <button type="button" @click="show.confirm = !show.confirm" class="text-slate-400 active:text-slate-600 shrink-0">
                    <svg v-if="show.confirm" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"/>
                    </svg>
                    <svg v-else class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"/><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                    </svg>
                  </button>
                </div>
              </div>
              <!-- Password mismatch message (mobile) -->
              <div v-if="!passwordsMatch && form.password_confirmation" class="px-4 pt-2 pb-3">
                <p class="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                  <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"/>
                  </svg>
                  Passwords do not match
                </p>
              </div>
              <div class="px-4 pb-4">
                <button type="submit" :disabled="loading || !isPasswordFormValid"
                  class="w-full bg-slate-800 dark:bg-slate-700 disabled:opacity-60 active:scale-[0.98]
                         text-white font-bold rounded-2xl py-3.5 text-sm transition">
                  {{ loading ? 'Updating…' : 'Update Password' }}
                </button>
              </div>
            </form>
          </div>
        </div>

        <!-- Danger Zone section -->
        <div>
          <p class="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider px-1 mb-2">Danger Zone</p>
          <div class="bg-white dark:bg-slate-900 rounded-2xl overflow-hidden shadow-sm border-2 border-red-200 dark:border-red-900">

            <!-- Warning Header -->
            <div class="px-4 py-3 bg-red-50 dark:bg-red-950/30 border-b border-red-200 dark:border-red-900">
              <div class="flex items-center gap-2">
                <svg class="w-4 h-4 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"/>
                </svg>
                <p class="text-xs font-bold text-red-900 dark:text-red-300">Irreversible Actions</p>
              </div>
            </div>

            <!-- Reset All Data -->
            <div class="px-4 py-4 border-b border-slate-100 dark:border-slate-800">
              <div class="flex items-center gap-3 mb-2">
                <div class="w-10 h-10 rounded-xl bg-orange-100 dark:bg-orange-950 flex items-center justify-center shrink-0">
                  <svg class="w-5 h-5 text-orange-600 dark:text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"/>
                  </svg>
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-bold text-slate-900 dark:text-white">Reset All Data</p>
                  <p class="text-xs text-slate-500 dark:text-slate-400">Start fresh with empty account</p>
                </div>
              </div>
              <p class="text-xs text-slate-600 dark:text-slate-400 mb-3 leading-relaxed">
                Delete all customers, products, invoices, payments, banks, and records. Your account stays active.
              </p>
              <button @click="showResetModal = true" type="button"
                class="w-full bg-orange-600 hover:bg-orange-700 active:scale-[0.98] text-white font-bold rounded-xl py-3 text-sm transition">
                Reset All Data
              </button>
            </div>

            <!-- Deactivate Account -->
            <div class="px-4 py-4">
              <div class="flex items-center gap-3 mb-2">
                <div class="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-950 flex items-center justify-center shrink-0">
                  <svg class="w-5 h-5 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"/>
                  </svg>
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-bold text-slate-900 dark:text-white">Deactivate Account</p>
                  <p class="text-xs text-slate-500 dark:text-slate-400">Permanently disable account</p>
                </div>
              </div>
              <p class="text-xs text-slate-600 dark:text-slate-400 mb-3 leading-relaxed">
                Close your account permanently. You'll be logged out and cannot sign in again.
              </p>
              <button @click="showDeactivateModal = true" type="button"
                class="w-full bg-red-600 hover:bg-red-700 active:scale-[0.98] text-white font-bold rounded-xl py-3 text-sm transition">
                Deactivate Account
              </button>
            </div>

          </div>
        </div>

      </div>
    </div>

    <!-- ── DESKTOP LAYOUT ── -->
    <div class="hidden md:block h-screen bg-white dark:bg-slate-950 overflow-hidden">

      <!-- Container with sidebar -->
      <div class="flex h-full">

        <!-- Sidebar Navigation - Fixed, non-scrollable -->
        <div class="w-64 border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex-shrink-0">
          <div class="py-6 px-4">
            <h2 class="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide px-3 mb-2">Settings</h2>
            <nav class="space-y-1">
              <button @click="activeTab = 'profile'" type="button"
                class="w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition"
                :class="activeTab === 'profile' ? 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"/>
                </svg>
                Business Profile
              </button>
              <button @click="activeTab = 'appearance'" type="button"
                class="w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition"
                :class="activeTab === 'appearance' ? 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.395m3.42 3.42a15.995 15.995 0 004.764-4.648l3.876-5.814a1.151 1.151 0 00-1.597-1.597L14.146 6.32a15.996 15.996 0 00-4.649 4.763m3.42 3.42a6.776 6.776 0 00-3.42-3.42"/>
                </svg>
                Appearance
              </button>
              <button @click="activeTab = 'account_mode'" type="button"
                class="w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition"
                :class="activeTab === 'account_mode' ? 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z"/>
                </svg>
                Account Mode
              </button>
              <button @click="activeTab = 'security'" type="button"
                class="w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition"
                :class="activeTab === 'security' ? 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"/>
                </svg>
                Security
              </button>
              <button @click="activeTab = 'mobile'" type="button"
                class="w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition"
                :class="activeTab === 'mobile' ? 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3"/>
                </svg>
                Mobile Apps
              </button>
              <button @click="activeTab = 'danger_zone'" type="button"
                class="w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition"
                :class="activeTab === 'danger_zone' ? 'bg-red-100 dark:bg-red-950/30 text-red-900 dark:text-red-400' : 'text-slate-600 dark:text-slate-400 hover:bg-red-50 dark:hover:bg-red-950/20 hover:text-red-700 dark:hover:text-red-400'">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"/>
                </svg>
                Danger Zone
              </button>
            </nav>
          </div>
        </div>

        <!-- Main Content Area - Scrollable -->
        <div class="flex-1 flex flex-col h-full overflow-hidden">

          <!-- Header - Fixed -->
          <div class="border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex-shrink-0">
            <div class="px-8 py-6">
              <h1 class="text-2xl font-semibold text-slate-900 dark:text-white">Settings</h1>
              <p class="text-sm text-slate-600 dark:text-slate-400 mt-1">Manage your account settings and preferences</p>
            </div>
          </div>

          <!-- Content - Scrollable -->
          <div class="flex-1 overflow-y-auto bg-slate-50 dark:bg-slate-950">
            <div class="px-8 py-6">

            <!-- Flash messages -->
            <div v-if="shouldShowFlash && ($page.props.flash?.notice || $page.props.flash?.alert)" class="mb-6">
              <div v-if="$page.props.flash?.notice" class="flex items-center gap-3 bg-emerald-50 dark:bg-emerald-950/50 border border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-400 px-4 py-3 rounded-md text-sm">
                <svg class="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <span class="font-medium">{{ $page.props.flash.notice }}</span>
              </div>
              <div v-if="$page.props.flash?.alert" class="flex items-center gap-3 bg-rose-50 dark:bg-rose-950/50 border border-rose-200 dark:border-rose-800 text-rose-800 dark:text-rose-400 px-4 py-3 rounded-md text-sm">
                <svg class="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                </svg>
                <span class="font-medium">{{ $page.props.flash.alert }}</span>
              </div>
            </div>

            <!-- Settings Sections -->
            <div class="max-w-4xl">

              <!-- Business Profile Section -->
              <div v-if="activeTab === 'profile'" class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md">
                <div class="px-6 py-4 border-b border-slate-200 dark:border-slate-800">
                  <h2 class="text-base font-semibold text-slate-900 dark:text-white">Public profile</h2>
                  <p class="text-sm text-slate-600 dark:text-slate-400 mt-1">This information will be displayed on your account</p>
                </div>

                <form @submit.prevent="submitProfile" class="divide-y divide-slate-200 dark:divide-slate-800">

                  <!-- Email (Read-only) -->
                  <div class="px-6 py-4">
                    <div class="flex flex-col sm:flex-row sm:items-start gap-4">
                      <label class="sm:w-1/4 text-sm font-medium text-slate-900 dark:text-white pt-2">Email</label>
                      <div class="sm:w-3/4">
                        <div class="text-sm text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-800 px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700">
                          {{ $page.props.auth.user.email }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Name -->
                  <div class="px-6 py-4">
                    <div class="flex flex-col sm:flex-row sm:items-start gap-4">
                      <label class="sm:w-1/4 text-sm font-medium text-slate-900 dark:text-white pt-2">Name</label>
                      <div class="sm:w-3/4">
                        <input v-model="profile.name" type="text" placeholder="Your full name"
                          class="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700
                                 text-slate-900 dark:text-white placeholder-slate-400 rounded-md px-3 py-2 text-sm
                                 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition" />
                      </div>
                    </div>
                  </div>

                  <!-- Business Name -->
                  <div class="px-6 py-4">
                    <div class="flex flex-col sm:flex-row sm:items-start gap-4">
                      <label class="sm:w-1/4 text-sm font-medium text-slate-900 dark:text-white pt-2">Business name</label>
                      <div class="sm:w-3/4">
                        <input v-model="profile.business_name" type="text" placeholder="Your business name"
                          class="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700
                                 text-slate-900 dark:text-white placeholder-slate-400 rounded-md px-3 py-2 text-sm
                                 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition" />
                      </div>
                    </div>
                  </div>

                  <!-- Business Address -->
                  <div class="px-6 py-4">
                    <div class="flex flex-col sm:flex-row sm:items-start gap-4">
                      <label class="sm:w-1/4 text-sm font-medium text-slate-900 dark:text-white pt-2">Business address</label>
                      <div class="sm:w-3/4">
                        <textarea v-model="profile.business_address" rows="3" placeholder="Your business address"
                          class="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700
                                 text-slate-900 dark:text-white placeholder-slate-400 rounded-md px-3 py-2 text-sm
                                 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition"></textarea>
                      </div>
                    </div>
                  </div>

                  <!-- Phone Number -->
                  <div class="px-6 py-4">
                    <div class="flex flex-col sm:flex-row sm:items-start gap-4">
                      <label class="sm:w-1/4 text-sm font-medium text-slate-900 dark:text-white pt-2">Phone number</label>
                      <div class="sm:w-3/4">
                        <input v-model="profile.business_phone_number" type="text" placeholder="Your phone number"
                          class="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700
                                 text-slate-900 dark:text-white placeholder-slate-400 rounded-md px-3 py-2 text-sm
                                 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition" />
                      </div>
                    </div>
                  </div>

                  <!-- Save Button -->
                  <div class="px-6 py-4 bg-slate-50 dark:bg-slate-800/50">
                    <div class="flex justify-end">
                      <button type="submit" :disabled="profileLoading"
                        class="bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed
                               text-white font-medium rounded-md px-4 py-2 text-sm transition">
                        {{ profileLoading ? 'Saving...' : 'Update profile' }}
                      </button>
                    </div>
                  </div>

                </form>
              </div>

              <!-- Appearance Section -->
              <div v-if="activeTab === 'appearance'" class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md">
                <div class="px-6 py-4 border-b border-slate-200 dark:border-slate-800">
                  <h2 class="text-base font-semibold text-slate-900 dark:text-white">Appearance</h2>
                  <p class="text-sm text-slate-600 dark:text-slate-400 mt-1">Customize how the interface looks for you</p>
                </div>

                <div class="px-6 py-6">
                  <label class="text-sm font-medium text-slate-900 dark:text-white block mb-4">Theme mode</label>

                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">

                    <!-- Light Mode Card -->
                    <button @click="!dark ? null : toggleDark()" type="button"
                      class="relative group overflow-hidden rounded-xl border-2 transition-all duration-200"
                      :class="!dark
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30 shadow-md'
                        : 'border-slate-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-sm'">

                      <!-- Preview Image Area -->
                      <div class="aspect-video bg-gradient-to-br from-slate-50 to-slate-100 p-4 relative">
                        <!-- Mini window mockup -->
                        <div class="bg-white rounded-lg shadow-lg h-full p-3 flex flex-col">
                          <div class="flex items-center gap-1.5 mb-2">
                            <div class="w-2 h-2 rounded-full bg-red-400"></div>
                            <div class="w-2 h-2 rounded-full bg-yellow-400"></div>
                            <div class="w-2 h-2 rounded-full bg-green-400"></div>
                          </div>
                          <div class="space-y-1.5 flex-1">
                            <div class="h-2 bg-slate-200 rounded w-3/4"></div>
                            <div class="h-2 bg-slate-200 rounded w-full"></div>
                            <div class="h-2 bg-slate-200 rounded w-5/6"></div>
                          </div>
                        </div>

                        <!-- Selected Badge -->
                        <div v-if="!dark" class="absolute top-2 right-2 bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg flex items-center gap-1">
                          <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/>
                          </svg>
                          Active
                        </div>
                      </div>

                      <!-- Card Content -->
                      <div class="p-4 bg-white dark:bg-slate-900">
                        <div class="flex items-center gap-3 mb-2">
                          <div class="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-950 flex items-center justify-center shrink-0">
                            <svg class="w-5 h-5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M12 3v1m0 16v1m8.66-9h-1M4.34 12h-1m15.07-6.07-.71.71M6.34 17.66l-.71.71m12.02 0-.71-.71M6.34 6.34l-.71-.71M12 7a5 5 0 100 10A5 5 0 0012 7z"/>
                            </svg>
                          </div>
                          <div class="text-left">
                            <h3 class="font-semibold text-slate-900 dark:text-white">Light Mode</h3>
                            <p class="text-xs text-slate-500 dark:text-slate-400">Bright and clear</p>
                          </div>
                        </div>
                        <p class="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                          A clean, bright interface perfect for well-lit environments and daytime use.
                        </p>
                      </div>

                      <!-- Selection Ring -->
                      <div v-if="!dark" class="absolute inset-0 rounded-xl ring-2 ring-blue-500 ring-offset-2 ring-offset-white dark:ring-offset-slate-900 pointer-events-none"></div>
                    </button>

                    <!-- Dark Mode Card -->
                    <button @click="dark ? null : toggleDark()" type="button"
                      class="relative group overflow-hidden rounded-xl border-2 transition-all duration-200"
                      :class="dark
                        ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/30 shadow-md'
                        : 'border-slate-200 dark:border-slate-700 hover:border-indigo-300 dark:hover:border-indigo-600 hover:shadow-sm'">

                      <!-- Preview Image Area -->
                      <div class="aspect-video bg-gradient-to-br from-slate-800 to-slate-900 p-4 relative">
                        <!-- Mini window mockup -->
                        <div class="bg-slate-800 rounded-lg shadow-lg h-full p-3 flex flex-col border border-slate-700">
                          <div class="flex items-center gap-1.5 mb-2">
                            <div class="w-2 h-2 rounded-full bg-red-400"></div>
                            <div class="w-2 h-2 rounded-full bg-yellow-400"></div>
                            <div class="w-2 h-2 rounded-full bg-green-400"></div>
                          </div>
                          <div class="space-y-1.5 flex-1">
                            <div class="h-2 bg-slate-600 rounded w-3/4"></div>
                            <div class="h-2 bg-slate-600 rounded w-full"></div>
                            <div class="h-2 bg-slate-600 rounded w-5/6"></div>
                          </div>
                        </div>

                        <!-- Selected Badge -->
                        <div v-if="dark" class="absolute top-2 right-2 bg-indigo-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg flex items-center gap-1">
                          <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/>
                          </svg>
                          Active
                        </div>
                      </div>

                      <!-- Card Content -->
                      <div class="p-4 bg-white dark:bg-slate-900">
                        <div class="flex items-center gap-3 mb-2">
                          <div class="w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-950 flex items-center justify-center shrink-0">
                            <svg class="w-5 h-5 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/>
                            </svg>
                          </div>
                          <div class="text-left">
                            <h3 class="font-semibold text-slate-900 dark:text-white">Dark Mode</h3>
                            <p class="text-xs text-slate-500 dark:text-slate-400">Easy on the eyes</p>
                          </div>
                        </div>
                        <p class="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                          A sleek, dark interface that reduces eye strain in low-light conditions.
                        </p>
                      </div>

                      <!-- Selection Ring -->
                      <div v-if="dark" class="absolute inset-0 rounded-xl ring-2 ring-indigo-500 ring-offset-2 ring-offset-white dark:ring-offset-slate-900 pointer-events-none"></div>
                    </button>

                  </div>

                  <!-- Info Box -->
                  <div class="mt-6 max-w-2xl">
                    <div class="flex gap-3 p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
                      <svg class="w-5 h-5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"/>
                      </svg>
                      <div>
                        <p class="text-sm font-medium text-blue-900 dark:text-blue-300">Theme Preference</p>
                        <p class="text-xs text-blue-700 dark:text-blue-400 mt-1">
                          Your theme preference is saved locally and will persist across sessions.
                        </p>
                      </div>
                    </div>
                  </div>

                </div>
              </div>

              <!-- Account Mode Section -->
              <div v-if="activeTab === 'account_mode'" class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md">
                <div class="px-6 py-4 border-b border-slate-200 dark:border-slate-800">
                  <h2 class="text-base font-semibold text-slate-900 dark:text-white">Account mode</h2>
                  <p class="text-sm text-slate-600 dark:text-slate-400 mt-1">Choose how you want to use this application</p>
                </div>

                <div class="p-6">
                  <div class="flex flex-col sm:flex-row sm:items-start gap-4">
                    <label class="sm:w-1/4 text-sm font-medium text-slate-900 dark:text-white pt-2">Mode selection</label>
                    <div class="sm:w-3/4 space-y-3">

                      <!-- Business Mode Option -->
                      <button @click="updateBusinessMode(true)" type="button"
                        class="w-full flex items-start gap-4 p-4 border-2 rounded-lg transition-all text-left"
                        :class="profile.business_mode === true ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30' : 'border-slate-300 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-700'">
                        <div class="flex-shrink-0 mt-0.5">
                          <div class="w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all"
                            :class="profile.business_mode === true ? 'border-blue-500 bg-blue-500' : 'border-slate-300 dark:border-slate-600'">
                            <div v-if="profile.business_mode === true" class="w-2 h-2 rounded-full bg-white"></div>
                          </div>
                        </div>
                        <div class="flex-1">
                          <div class="flex items-center gap-2">
                            <svg class="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z"/>
                            </svg>
                            <p class="font-semibold text-slate-900 dark:text-white">Business Mode</p>
                          </div>
                          <p class="text-sm text-slate-600 dark:text-slate-400 mt-1">
                            Full access to invoices, payments, customers, products, and business reports
                          </p>
                        </div>
                      </button>

                      <!-- Personal Mode Option -->
                      <button @click="updateBusinessMode(false)" type="button"
                        class="w-full flex items-start gap-4 p-4 border-2 rounded-lg transition-all text-left"
                        :class="profile.business_mode === false ? 'border-green-500 bg-green-50 dark:bg-green-950/30' : 'border-slate-300 dark:border-slate-700 hover:border-green-300 dark:hover:border-green-700'">
                        <div class="flex-shrink-0 mt-0.5">
                          <div class="w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all"
                            :class="profile.business_mode === false ? 'border-green-500 bg-green-500' : 'border-slate-300 dark:border-slate-600'">
                            <div v-if="profile.business_mode === false" class="w-2 h-2 rounded-full bg-white"></div>
                          </div>
                        </div>
                        <div class="flex-1">
                          <div class="flex items-center gap-2">
                            <svg class="w-5 h-5 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"/>
                            </svg>
                            <p class="font-semibold text-slate-900 dark:text-white">Personal Mode</p>
                          </div>
                          <p class="text-sm text-slate-600 dark:text-slate-400 mt-1">
                            Track personal income, expenses, banks, and wealth. Business features will be hidden.
                          </p>
                        </div>
                      </button>

                      <!-- Info Box -->
                      <div class="mt-4 p-4 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800">
                        <div class="flex gap-3">
                          <svg class="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"/>
                          </svg>
                          <div>
                            <p class="text-sm font-medium text-amber-900 dark:text-amber-300">Note</p>
                            <p class="text-xs text-amber-700 dark:text-amber-400 mt-1">
                              You can change this setting anytime. Switching modes will show/hide relevant features in the navigation menu.
                            </p>
                          </div>
                        </div>
                      </div>

                    </div>
                  </div>
                </div>
              </div>

              <!-- Security Section -->
              <div v-if="activeTab === 'security'" class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md">
                <div class="px-6 py-4 border-b border-slate-200 dark:border-slate-800">
                  <h2 class="text-base font-semibold text-slate-900 dark:text-white">Change password</h2>
                  <p class="text-sm text-slate-600 dark:text-slate-400 mt-1">Update your password to keep your account secure</p>
                </div>

                <form @submit.prevent="submit" class="divide-y divide-slate-200 dark:divide-slate-800">

                  <!-- Current Password -->
                  <div class="px-6 py-4">
                    <div class="flex flex-col sm:flex-row sm:items-start gap-4">
                      <label class="sm:w-1/4 text-sm font-medium text-slate-900 dark:text-white pt-2">Old password</label>
                      <div class="sm:w-3/4">
                        <div class="relative max-w-md">
                          <input
                            v-model="form.current_password"
                            :type="show.current ? 'text' : 'password'"
                            placeholder="Enter your current password"
                            autocomplete="current-password"
                            required
                            class="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700
                                   text-slate-900 dark:text-white placeholder-slate-400 rounded-md px-3 py-2 pr-10 text-sm
                                   focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                          />
                          <button type="button" @click="show.current = !show.current"
                            class="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 p-1">
                            <svg v-if="show.current" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"/>
                            </svg>
                            <svg v-else class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"/><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- New Password -->
                  <div class="px-6 py-4">
                    <div class="flex flex-col sm:flex-row sm:items-start gap-4">
                      <label class="sm:w-1/4 text-sm font-medium text-slate-900 dark:text-white pt-2">New password</label>
                      <div class="sm:w-3/4">
                        <div class="relative max-w-md">
                          <input
                            v-model="form.password"
                            :type="show.password ? 'text' : 'password'"
                            placeholder="Enter new password"
                            autocomplete="new-password"
                            required
                            class="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700
                                   text-slate-900 dark:text-white placeholder-slate-400 rounded-md px-3 py-2 pr-10 text-sm
                                   focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                          />
                          <button type="button" @click="show.password = !show.password"
                            class="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 p-1">
                            <svg v-if="show.password" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"/>
                            </svg>
                            <svg v-else class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"/><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Confirm Password -->
                  <div class="px-6 py-4">
                    <div class="flex flex-col sm:flex-row sm:items-start gap-4">
                      <label class="sm:w-1/4 text-sm font-medium text-slate-900 dark:text-white pt-2">Confirm password</label>
                      <div class="sm:w-3/4">
                        <div class="relative max-w-md">
                          <input
                            v-model="form.password_confirmation"
                            :type="show.confirm ? 'text' : 'password'"
                            placeholder="Confirm your new password"
                            autocomplete="new-password"
                            required
                            class="w-full bg-white dark:bg-slate-900 border rounded-md px-3 py-2 pr-10 text-sm
                                   text-slate-900 dark:text-white placeholder-slate-400
                                   focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                            :class="!passwordsMatch ? 'border-red-500 dark:border-red-500' : 'border-slate-300 dark:border-slate-700'"
                          />
                          <button type="button" @click="show.confirm = !show.confirm"
                            class="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 p-1">
                            <svg v-if="show.confirm" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"/>
                            </svg>
                            <svg v-else class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"/><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                            </svg>
                          </button>
                        </div>
                        <!-- Password mismatch message -->
                        <p v-if="!passwordsMatch && form.password_confirmation" class="text-xs text-red-600 dark:text-red-400 mt-2 flex items-center gap-1">
                          <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"/>
                          </svg>
                          Passwords do not match
                        </p>
                      </div>
                    </div>
                  </div>

                  <!-- Update Button -->
                  <div class="px-6 py-4 bg-slate-50 dark:bg-slate-800/50">
                    <div class="flex justify-end">
                      <button
                        type="submit"
                        :disabled="loading || !isPasswordFormValid"
                        class="bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed
                               text-white font-medium rounded-md px-4 py-2 text-sm transition">
                        {{ loading ? 'Updating...' : 'Update password' }}
                      </button>
                    </div>
                  </div>

                </form>
              </div>


              <!-- Danger Zone Section -->
              <div v-if="activeTab === 'danger_zone'" class="bg-white dark:bg-slate-900 border border-red-200 dark:border-red-800 rounded-md">
                <div class="px-6 py-4 border-b border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30">
                  <div class="flex items-center gap-3">
                    <svg class="w-5 h-5 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"/>
                    </svg>
                    <div>
                      <h2 class="text-base font-semibold text-red-900 dark:text-red-300">Danger zone</h2>
                      <p class="text-sm text-red-700 dark:text-red-400 mt-1">Irreversible and destructive actions</p>
                    </div>
                  </div>
                </div>

                <div class="divide-y divide-slate-200 dark:divide-slate-800">

                  <!-- Reset All Data -->
                  <div class="px-6 py-6">
                    <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                      <div class="flex-1">
                        <div class="flex items-center gap-2 mb-1">
                          <svg class="w-5 h-5 text-orange-600 dark:text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"/>
                          </svg>
                          <h3 class="text-sm font-semibold text-slate-900 dark:text-white">Reset all data</h3>
                        </div>
                        <p class="text-sm text-slate-600 dark:text-slate-400">
                          Permanently delete all your data including customers, products, invoices, payments, banks, income, expenses, and wealth records. Your account will remain active with a fresh start.
                        </p>
                      </div>
                      <button @click="showResetModal = true" type="button"
                        class="sm:shrink-0 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-md px-4 py-2 text-sm transition whitespace-nowrap">
                        Reset Data
                      </button>
                    </div>
                  </div>

                  <!-- Deactivate Account -->
                  <div class="px-6 py-6">
                    <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                      <div class="flex-1">
                        <div class="flex items-center gap-2 mb-1">
                          <svg class="w-5 h-5 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"/>
                          </svg>
                          <h3 class="text-sm font-semibold text-slate-900 dark:text-white">Deactivate account</h3>
                        </div>
                        <p class="text-sm text-slate-600 dark:text-slate-400">
                          Permanently disable your account. This will log you out immediately and you will not be able to sign in again. This action cannot be undone.
                        </p>
                      </div>
                      <button @click="showDeactivateModal = true" type="button"
                        class="sm:shrink-0 bg-red-600 hover:bg-red-700 text-white font-medium rounded-md px-4 py-2 text-sm transition whitespace-nowrap">
                        Deactivate Account
                      </button>
                    </div>
                  </div>

                </div>
              </div>

              <!-- Mobile Apps Section -->
              <div v-if="activeTab === 'mobile'" class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md">
                <div class="px-6 py-4 border-b border-slate-200 dark:border-slate-800">
                  <h2 class="text-base font-semibold text-slate-900 dark:text-white">Mobile applications</h2>
                  <p class="text-sm text-slate-600 dark:text-slate-400 mt-1">Download and install apps for your mobile devices</p>
                </div>

                <div class="divide-y divide-slate-200 dark:divide-slate-800">

                  <!-- Android App -->
                  <div class="px-6 py-4">
                    <div class="flex items-center justify-between">
                      <div class="flex items-center gap-3">
                        <div class="w-10 h-10 rounded-md bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                          <svg class="w-5 h-5 text-emerald-600 dark:text-emerald-400" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M17.523 15.341a.75.75 0 01-.75-.75V9.41a.75.75 0 011.5 0v5.181a.75.75 0 01-.75.75zm-11.046 0a.75.75 0 01-.75-.75V9.41a.75.75 0 011.5 0v5.181a.75.75 0 01-.75.75zM8.5 7.5h7a4.5 4.5 0 014.5 4.5v3a4.5 4.5 0 01-4.5 4.5h-7A4.5 4.5 0 014 15V12a4.5 4.5 0 014.5-4.5zm1.25 5.25a.75.75 0 100-1.5.75.75 0 000 1.5zm4.5 0a.75.75 0 100-1.5.75.75 0 000 1.5zM8.318 6.475l-1.17-2.03a.375.375 0 01.65-.374l1.2 2.08A6.48 6.48 0 0112 5.5c1.08 0 2.1.263 2.996.648l1.2-2.08a.375.375 0 01.65.375l-1.17 2.03A6.503 6.503 0 0118.5 10H5.5a6.503 6.503 0 012.818-3.525z"/>
                          </svg>
                        </div>
                        <div>
                          <p class="text-sm font-semibold text-slate-900 dark:text-white">Android</p>
                          <p class="text-xs text-slate-500 dark:text-slate-400">Version 1.0 · APK</p>
                        </div>
                      </div>
                      <a href="/apps/android/master_ledger.apk" download
                        class="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-md px-3 py-1.5 text-sm transition">
                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                          <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"/>
                        </svg>
                        Download
                      </a>
                    </div>
                  </div>

                  <!-- iOS App -->
                  <div class="px-6 py-4">
                    <div class="flex items-center justify-between opacity-50">
                      <div class="flex items-center gap-3">
                        <div class="w-10 h-10 rounded-md bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                          <svg class="w-5 h-5 text-slate-400" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                          </svg>
                        </div>
                        <div>
                          <p class="text-sm font-semibold text-slate-500 dark:text-slate-400">iOS</p>
                          <p class="text-xs text-slate-400">Coming soon</p>
                        </div>
                      </div>
                      <span class="text-xs font-medium text-slate-500 dark:text-slate-400 px-3 py-1.5 bg-slate-100 dark:bg-slate-800 rounded-md">
                        Soon
                      </span>
                    </div>
                  </div>

                  <!-- Installation Note -->
                  <div class="px-6 py-4 bg-blue-50 dark:bg-blue-950/30">
                    <div class="flex gap-3">
                      <svg class="w-5 h-5 text-blue-600 dark:text-blue-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"/>
                      </svg>
                      <div>
                        <p class="text-sm font-medium text-blue-900 dark:text-blue-300">Installation instructions</p>
                        <p class="text-xs text-blue-700 dark:text-blue-400 mt-1">
                          Enable "Install unknown apps" in Settings → Security before installing the Android APK.
                        </p>
                      </div>
                    </div>
                  </div>

                </div>
              </div>

            </div><!-- end sections container -->

            </div><!-- end px-8 py-6 -->
          </div><!-- end scrollable content -->

        </div><!-- end main content area -->

      </div><!-- end flex container -->

    </div><!-- end desktop -->

  </AppLayout>

  <!-- Modals -->
  <ResetDataModal
    :show="showResetModal"
    :password="resetPassword"
    :error="resetPasswordError"
    :loading="resetLoading"
    @close="handleResetClose"
    @confirm="confirmReset"
    @update:password="resetPassword = $event"
    @update:error="resetPasswordError = $event"
  />

  <DeactivateAccountModal
    :show="showDeactivateModal"
    :password="deactivatePassword"
    :reason="deletionReason"
    :error="deactivatePasswordError"
    :loading="deactivateLoading"
    @close="handleDeactivateClose"
    @confirm="confirmDeactivate"
    @update:password="deactivatePassword = $event"
    @update:reason="deletionReason = $event"
    @update:error="deactivatePasswordError = $event"
  />
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useForm, usePage, router } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'
import ResetDataModal from '@/components/settings/ResetDataModal.vue'
import DeactivateAccountModal from '@/components/settings/DeactivateAccountModal.vue'

const props = defineProps({ user: Object })
const page  = usePage()
const loading = ref(false)
const businessName = computed(() => page.props.auth?.user?.business_name?.trim() || 'Master Ledger')

const dark = ref(false)
const activeTab = ref('profile')

// Valid section names
const validSections = ['profile', 'appearance', 'account_mode', 'security', 'mobile', 'danger_zone']

// Track which section had the flash message
const flashSection = ref(null)

// Initialize active tab from URL or default to 'profile'
function initializeTab() {
  const urlParams = new URLSearchParams(window.location.search)
  const section = urlParams.get('section')

  if (section && validSections.includes(section)) {
    activeTab.value = section
  } else {
    activeTab.value = 'profile'
  }
}

// Update URL when tab changes
watch(activeTab, (newTab, oldTab) => {
  // Skip if it's the same tab (initial load)
  if (newTab === oldTab) return

  const url = new URL(window.location)
  url.searchParams.set('section', newTab)
  window.history.pushState({}, '', url)
})

onMounted(() => {
  document.title = `Settings | ${businessName.value}`
  dark.value = localStorage.getItem('theme') === 'dark'
  initializeTab()

  // Listen for popstate (browser back/forward)
  window.addEventListener('popstate', () => {
    initializeTab()
    // Clear flash section on navigation
    flashSection.value = null
  })
})

function toggleDark() {
  dark.value = !dark.value
  localStorage.setItem('theme', dark.value ? 'dark' : 'light')
  document.documentElement.classList.toggle('dark', dark.value)
}

const profileLoading = ref(false)

const userInitial = computed(() =>
  page.props.auth.user?.email?.charAt(0).toUpperCase()
)

// ── Business info ────────────────────────────────────
const profile = ref({
  name:                  props.user?.name                  || '',
  business_name:         props.user?.business_name         || '',
  business_address:      props.user?.business_address      || '',
  business_phone_number: props.user?.business_phone_number || '',
  business_mode:         props.user?.business_mode,
})

function submitProfile() {
  profileLoading.value = true
  useForm({ user: profile.value }).patch('/settings/profile', {
    onFinish: () => { profileLoading.value = false },
  })
}

function updateBusinessMode(mode) {
  profile.value.business_mode = mode
  // Rails controller will redirect to settings with account_mode section
  // and the flash message will be preserved
  useForm({ user: { business_mode: mode } }).patch('/settings/business_mode')
}

// ── Password ─────────────────────────────────────────
const show = ref({ current: false, password: false, confirm: false })

const form = ref({
  current_password: '',
  password: '',
  password_confirmation: '',
})

// Check if passwords match
const passwordsMatch = computed(() => {
  if (!form.value.password || !form.value.password_confirmation) return true
  return form.value.password === form.value.password_confirmation
})

// Check if form is valid
const isPasswordFormValid = computed(() => {
  return form.value.current_password.trim() !== '' &&
         form.value.password.trim() !== '' &&
         form.value.password_confirmation.trim() !== '' &&
         passwordsMatch.value
})

function submit() {
  if (!isPasswordFormValid.value) return

  loading.value = true
  useForm({ user: form.value }).patch('/settings/password', {
    onFinish: () => {
      loading.value = false
      form.value = { current_password: '', password: '', password_confirmation: '' }
      show.value = { current: false, password: false, confirm: false }
    },
  })
}

// ── Danger Zone ──────────────────────────────────────
// Modal states
const showResetModal = ref(false)
const showDeactivateModal = ref(false)
const resetLoading = ref(false)
const deactivateLoading = ref(false)

// Form data
const resetPassword = ref('')
const deactivatePassword = ref('')
const deletionReason = ref('')
const resetPasswordError = ref('')
const deactivatePasswordError = ref('')

// Close handlers
function handleResetClose() {
  showResetModal.value = false
  resetPassword.value = ''
  resetPasswordError.value = ''
}

function handleDeactivateClose() {
  showDeactivateModal.value = false
  deactivatePassword.value = ''
  deletionReason.value = ''
  deactivatePasswordError.value = ''
}

// Confirm handlers
function confirmReset() {
  if (!resetPassword.value) return

  resetLoading.value = true
  resetPasswordError.value = ''

  router.delete('/settings/reset_all_data', {
    data: { password: resetPassword.value },
    preserveScroll: true,
    onSuccess: () => {
      resetLoading.value = false
      handleResetClose()
    },
    onError: () => {
      resetLoading.value = false
      // Error will be set by watcher
    }
  })
}

function confirmDeactivate() {
  if (!deactivatePassword.value) return

  deactivateLoading.value = true
  deactivatePasswordError.value = ''

  router.delete('/settings/deactivate_account', {
    data: {
      password: deactivatePassword.value,
      deletion_reason: deletionReason.value
    },
    preserveScroll: true,
    onSuccess: (page) => {
      // Check if redirected to login page (successful deactivation)
      if (page.component === 'auth/Login' || window.location.pathname.includes('sign_in')) {
        // Force full page reload to login
        window.location.href = '/users/sign_in'
      } else {
        deactivateLoading.value = false
        handleDeactivateClose()
      }
    },
    onError: () => {
      deactivateLoading.value = false
      // Error will be set by watcher
    },
    onFinish: (visit) => {
      // If we got redirected to login, do a full page visit
      if (visit.url.includes('sign_in')) {
        window.location.href = visit.url
      }
    }
  })
}

// Watcher for flash messages to detect password errors
watch(() => page.props.flash?.alert, (newAlert) => {
  if (newAlert) {
    // Check if it's a password error for reset modal
    if (showResetModal.value && newAlert.toLowerCase().includes('password')) {
      resetPasswordError.value = newAlert
    }
    // Check if it's a password error for deactivate modal
    if (showDeactivateModal.value && newAlert.toLowerCase().includes('password')) {
      deactivatePasswordError.value = newAlert
    }
  }
})

// Update shouldShowFlash to hide flash when password error and modal is open
const shouldShowFlash = computed(() => {
  // If no flash section is set, check if there's a flash message
  if (flashSection.value === null && (page.props.flash?.notice || page.props.flash?.alert)) {
    // Set flash section to current active tab on initial load
    flashSection.value = activeTab.value
  }

  // Hide flash if modal is open and error is a password error
  if (page.props.flash?.alert) {
    const alert = page.props.flash.alert.toLowerCase()
    if ((showResetModal.value || showDeactivateModal.value) && alert.includes('password')) {
      return false
    }
  }

  // Show flash only if we're on the same section where flash was created
  return flashSection.value === activeTab.value
})
</script>
